import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const PILLARS = [
  { num: "01", label: "Atmospheric Dominance" },
  { num: "02", label: "Premium Media Assets" },
  { num: "03", label: "High-Impact Visuals" },
  { num: "04", label: "Citywide Branding" },
];

const WhatWeDo = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setShown(true), obs.disconnect()),
      { threshold: 0.15 }
    );
    const node = ref.current;
    if (node) obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white text-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Eyebrow + heading */}
          <div className={`lg:col-span-5 reveal ${shown ? "in" : ""}`}>
            <p className="text-[11px] uppercase tracking-[0.32em] text-black/55 mb-6">
              What We Do
            </p>
            <h2 className="font-display text-[12vw] md:text-[6vw] lg:text-[5.2vw] uppercase leading-[0.92]">
              Immersive <br />
              <span className="font-serif-display italic font-black">brand</span>{" "}
              <br className="hidden lg:block" />
              experiences.
            </h2>

            <div className="mt-10 hidden lg:grid grid-cols-2 gap-y-6 gap-x-4 max-w-md">
              {PILLARS.map((p) => (
                <div
                  key={p.num}
                  className="flex items-baseline gap-3 border-t border-black/15 pt-3"
                >
                  <span className="text-[11px] uppercase tracking-[0.28em] text-black/45 tabular-nums">
                    {p.num}
                  </span>
                  <span className="text-[13px] uppercase tracking-[0.14em] font-semibold">
                    {p.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Description */}
          <div
            className={`lg:col-span-7 reveal ${shown ? "in" : ""}`}
            style={{ transitionDelay: "150ms" }}
          >
            <div className="lg:pl-12 lg:border-l lg:border-black/15">
              <p className="text-2xl md:text-3xl lg:text-[1.85rem] leading-[1.45] text-black/85 font-light">
                We create{" "}
                <span className="font-serif-display italic font-black text-black">
                  immersive brand experiences
                </span>{" "}
                that give our clients{" "}
                <span className="text-black font-medium">
                  atmospheric dominance
                </span>{" "}
                in outdoor advertising.
              </p>

              <div className="mt-8 space-y-6 text-black/70 leading-relaxed max-w-2xl text-[17px]">
                <p>
                  Through strategically placed premium media assets, high-impact
                  visuals, and citywide branding solutions, we help brands
                  command attention, maximize visibility, and create lasting
                  recall across the urban landscape.
                </p>
                <p>
                  Our focus is not just on advertising spaces, but on building a
                  powerful presence that audiences see, remember, and engage
                  with.
                </p>
              </div>

              {/* Mobile pillar list */}
              <div className="mt-10 grid grid-cols-2 gap-y-5 gap-x-4 lg:hidden">
                {PILLARS.map((p) => (
                  <div
                    key={p.num}
                    className="flex items-baseline gap-3 border-t border-black/15 pt-3"
                  >
                    <span className="text-[11px] uppercase tracking-[0.28em] text-black/45 tabular-nums">
                      {p.num}
                    </span>
                    <span className="text-[13px] uppercase tracking-[0.14em] font-semibold">
                      {p.label}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to="/media"
                className="mt-12 inline-flex items-center gap-3 text-sm uppercase tracking-[0.22em] font-semibold px-6 py-4 border border-black hover:bg-black hover:text-white transition-colors duration-200"
              >
                See How We Do It
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
